/**
 * Width limits of the docked assistant panel, in CSS pixels.
 *
 * These numbers live here and nowhere else: the panel reads them for its
 * inline `--assistant-panel-width`, the store for clamping, the resize handle
 * for its ARIA range. A CSS fallback would be a second copy of the default.
 */
export const ASSISTANT_PANEL_WIDTH_PRESETS = [380, 560] as const;
export const ASSISTANT_PANEL_DEFAULT_WIDTH: number = ASSISTANT_PANEL_WIDTH_PRESETS[0];
export const ASSISTANT_PANEL_MIN_WIDTH = 320;
export const ASSISTANT_PANEL_MAX_WIDTH = 720;
export const ASSISTANT_PANEL_MAX_VIEWPORT_RATIO = 0.6;
export const ASSISTANT_PANEL_KEYBOARD_STEP = 16;

export function getAssistantPanelMaxWidth(viewportWidth: number): number {
  const viewportShare = Math.floor(viewportWidth * ASSISTANT_PANEL_MAX_VIEWPORT_RATIO);
  return Math.max(ASSISTANT_PANEL_MIN_WIDTH, Math.min(ASSISTANT_PANEL_MAX_WIDTH, viewportShare));
}

export function clampAssistantPanelWidth(width: number, viewportWidth: number): number {
  if (!Number.isFinite(width)) {
    return ASSISTANT_PANEL_DEFAULT_WIDTH;
  }

  const max = getAssistantPanelMaxWidth(viewportWidth);
  return Math.round(Math.min(max, Math.max(ASSISTANT_PANEL_MIN_WIDTH, width)));
}

/** Wide means at or beyond the wide preset, or at the clamp when that preset is out of reach. */
export function isAssistantPanelWide(width: number, viewportWidth: number): boolean {
  const wideTarget = Math.min(
    ASSISTANT_PANEL_WIDTH_PRESETS[1],
    getAssistantPanelMaxWidth(viewportWidth)
  );
  return width >= wideTarget;
}

/** The preset the header toggle jumps to from the current width. */
export function getAssistantPanelPresetToggle(width: number, viewportWidth: number): number {
  return isAssistantPanelWide(width, viewportWidth)
    ? ASSISTANT_PANEL_WIDTH_PRESETS[0]
    : ASSISTANT_PANEL_WIDTH_PRESETS[1];
}
